import { useMutation } from "@apollo/client";
import { Edit, Eye } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { UPDATE_SALON_REVIEW_MUTATION } from "@/graphql/salon-reviews";
import { timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { useReviewUIStore } from "../store/use-review-ui-store";
import type { SalonReviewItem } from "../types";
import { GoogleReviewReplyComposer } from "./google-review-reply-composer";
import { StarRatingDisplay } from "./star-rating-display";

interface SalonReviewRowProps {
  review: SalonReviewItem;
  /**
   * Hides the edit affordance for manual (Platform) reviews. The Last
   * Reviews preview on the Customer Service overview uses this — the row
   * there is a read-only glance and a clickable edit pencil among other
   * items invites accidental clicks. The Platform detail page still
   * surfaces edit (default behavior) for actual editing.
   */
  hideEditAction?: boolean;
}

export function SalonReviewRow({ review, hideEditAction = false }: SalonReviewRowProps) {
  const { t } = useTranslation("settings");
  const { openEditDialog } = useReviewUIStore();
  const [markAsRead] = useMutation(UPDATE_SALON_REVIEW_MUTATION, {
    refetchQueries: ["SalonReviews", "SalonReviewStats"],
  });

  const reviewTime = timezoneUtils.fromISO(review.reviewTime);
  const formattedDate = reviewTime.toFormat("MMM dd, yyyy • h:mm a");
  const isGoogle = review.source === "GOOGLE";
  const isUnreplied = isGoogle && !review.google?.replyComment;
  const canMarkAsRead = isUnreplied && !review.isSnoozed;
  const replyTime = review.google?.replyTime
    ? timezoneUtils.fromISO(review.google.replyTime).toFormat("MMM dd, yyyy")
    : null;

  return (
    <Card className={cn(review.isHidden && "opacity-60")}>
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {isGoogle ? (
              <Avatar className="h-7 w-7">
                <AvatarImage src={review.google?.reviewerPhotoUrl ?? undefined} alt="" />
                <AvatarFallback>{review.reviewerName.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            ) : null}
            <h3 className="font-semibold">{review.reviewerName}</h3>
            <StarRatingDisplay rating={review.starRating} />
            {isGoogle && (
              <Badge variant="outline" className="text-xs">
                {t("reviews.fromGoogle")}
              </Badge>
            )}
            {review.isHidden && <Badge variant="secondary">{t("reviews.badges.hidden")}</Badge>}
            {review.isSnoozed && (
              <Badge variant="secondary" className="text-xs">
                {t("reviews.actions.markedAsRead")}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
          {review.comment && <CollapsibleComment comment={review.comment} />}
          {review.translatedComment && (
            <div className="mt-1 rounded-md border-l-2 border-blue-300 bg-blue-50/50 dark:bg-blue-950/20 p-2 text-sm">
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                {t("reviews.translatedComment")}
              </div>
              <CollapsibleComment comment={review.translatedComment} isReply />
            </div>
          )}
          {isGoogle && review.google?.replyComment && (
            <div className="mt-2 rounded-md border-l-2 border-primary/40 bg-muted/30 p-2 text-sm">
              <div className="text-xs font-medium text-muted-foreground">
                {t("reviews.ownerReply")}
                {replyTime ? ` • ${replyTime}` : ""}
              </div>
              <CollapsibleComment comment={review.google.replyComment} isReply />
            </div>
          )}
          {isGoogle && (
            <div className="flex items-center justify-between pt-1">
              <GoogleReviewReplyComposer
                reviewId={review.id}
                reviewerName={review.reviewerName}
                existingReply={review.google?.replyComment ?? null}
                salonId={review.salonId}
              />
              {canMarkAsRead && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    markAsRead({
                      variables: {
                        salonId: review.salonId,
                        id: review.id,
                        input: { isSnoozed: true },
                      },
                    })
                  }
                  aria-label={t("reviews.actions.markAsRead")}
                >
                  <Eye className="h-3.5 w-3.5" />
                  <span className="ml-1 text-xs">{t("reviews.actions.markAsRead")}</span>
                </Button>
              )}
            </div>
          )}
        </div>

        {!isGoogle && !hideEditAction && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditDialog(review.id)}
              aria-label={t("reviews.actions.edit")}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CollapsibleComment({ comment, isReply }: { comment: string; isReply?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const lines = comment.split("\n").length;
  const canExpand = lines > 5 || comment.length > 250;

  return (
    <div className="space-y-1">
      <p
        className={cn(
          "whitespace-pre-wrap text-sm",
          isReply ? "text-foreground/80" : "text-foreground/90",
          !expanded && canExpand && "line-clamp-5"
        )}
      >
        {comment}
      </p>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-sm font-medium text-primary hover:underline p-0"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
